"use client"

import * as React from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { MobileScreenHeaderProvider } from "@ibada/ui"

import { useActiveWorkspaceSlug, useCurrentWorkspaceAccess } from "@/features/auth"
import { useTenantNavSections } from "@/features/dashboard/hooks/use-tenant-nav-sections"
import type { ChurchNavSection } from "@/features/dashboard/model/church-nav"
import {
  isMobileRootPath,
  resolveMobileSectionRoot,
  selectMobilePrimaryTabs,
} from "@/features/dashboard/model/mobile-screens"
import { useTranslations } from "@/i18n"
import { hasInAppHistory } from "@/shared/hooks/use-create-page-exit"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"

/** Where a phone screen was opened from, carried in the router state of the link that opened it. */
export enum MobileNavigationOrigin {
  /** The ☰ menu page: back from a screen opened there returns Home. */
  Menu = "menu",
}

export type MobileNavigationState = { mobileOrigin?: MobileNavigationOrigin }

/** Router state for a link in the ☰ menu, so the screen it opens knows its back goes Home. */
export const MOBILE_MENU_LINK_STATE: MobileNavigationState = { mobileOrigin: MobileNavigationOrigin.Menu }

/**
 * The sections this user sees in the phone nav. A department-focused user sees only Departments (the sidebar
 * applies the same rule), so their tabs and root screens follow it too.
 */
export function useMobileNavSections(): ChurchNavSection[] {
  const { sections } = useTenantNavSections()
  const access = useCurrentWorkspaceAccess()
  const focused = access?.departmentEntry?.mode === "FOCUSED"
  return React.useMemo(
    () => (focused ? sections.filter((section) => section.url === "/departments") : sections),
    [focused, sections],
  )
}

/** The bottom tabs for this user: one source for the tab bar, root screens and back fallbacks. */
export function useMobilePrimaryTabs(): ChurchNavSection[] {
  const sections = useMobileNavSections()
  return React.useMemo(() => selectMobilePrimaryTabs(sections), [sections])
}

/**
 * True on a ROOT screen: a bottom tab's landing (Home, Members, Finance, Activities for most users). Root screens
 * carry the big app header (menu, church, bell, avatar); every other screen is a stack screen with a back button.
 */
export function useIsMobileRootScreen(): boolean {
  const tabs = useMobilePrimaryTabs()
  const { pathname } = useLocation()
  return React.useMemo(() => isMobileRootPath(pathname, tabs), [pathname, tabs])
}

/**
 * The one back rule for phone stack screens:
 * 1. opened from the ☰ menu: back goes Home;
 * 2. reached by navigating inside the app: back goes to the previous screen (real history);
 * 3. opened directly (a deep link, a notification, a refresh): back goes to `fallback` (an admin path such as
 *    "/finance"), else the root of the tab this screen belongs to, else Home.
 */
export function useMobileBack({ fallback }: { fallback?: string } = {}): () => void {
  const location = useLocation()
  const navigate = useNavigate()
  const tabs = useMobilePrimaryTabs()
  const { branchSlug } = useTenantNavSections()
  const workspaceSlug = useActiveWorkspaceSlug()

  return React.useCallback(() => {
    const toAdmin = (path: string) => navigate(buildBranchAdminUrl(branchSlug, path, workspaceSlug))
    const state = location.state as MobileNavigationState | null
    if (state?.mobileOrigin === MobileNavigationOrigin.Menu) {
      toAdmin("/")
      return
    }
    if (hasInAppHistory(location)) {
      navigate(-1)
      return
    }
    toAdmin(fallback ?? resolveMobileSectionRoot(location.pathname, tabs) ?? "/")
  }, [branchSlug, fallback, location, navigate, tabs, workspaceSlug])
}

/**
 * Wraps the page content of the app shell. On a phone stack screen it hands every shared header
 * (MobileScreenHeader, the DataTable's title row) a default back button following {@link useMobileBack}; on a
 * root screen, and on desktop, there is none.
 */
export function MobileScreenShell({ children }: { children: React.ReactNode }) {
  const t = useTranslations("churchNav.mobile")
  const isMobile = useIsMobile()
  const isRoot = useIsMobileRootScreen()
  const goBack = useMobileBack()
  const label = t("back")
  const value = React.useMemo(
    () => ({ defaultBack: isMobile && !isRoot ? { label, onBack: goBack } : null }),
    [goBack, isMobile, isRoot, label],
  )
  return <MobileScreenHeaderProvider value={value}>{children}</MobileScreenHeaderProvider>
}
